import type { AnyFieldApi } from "@tanstack/solid-form";
import {
  type ComponentProps,
  createMemo,
  onMount,
  Show,
  splitProps,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Input } from "@/components/ui/input";
import { cn } from "@/libs/cn";
import {
  convertDailyToHourly,
  formatCurrency,
  parseInputValue,
} from "@/libs/format";

type RateInputProps = Omit<
  ComponentProps<"input">,
  "onChange" | "onInput" | "onBlur" | "onFocus"
> & {
  fieldApi: AnyFieldApi;
  currency?: string;
};

export const RateInput = (props: RateInputProps) => {
  const { t } = useI18n();
  const [localProps, restProps] = splitProps(props, [
    "fieldApi",
    "currency",
    "class",
    "classList",
    "disabled",
  ]);

  let inputRef: HTMLInputElement | undefined;

  const handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const cleanValue = parseInputValue(target.value);
    const numValue = Number.parseFloat(cleanValue);

    if (Number.isNaN(numValue)) {
      localProps.fieldApi.handleChange(cleanValue);
      return;
    }

    localProps.fieldApi.handleChange(numValue.toString());
  };

  const displayValue = createMemo(() => {
    const currentValue = localProps.fieldApi.state.value;

    if (!currentValue || currentValue === "") return "";

    const dailyValue = Number.parseFloat(parseInputValue(currentValue));

    if (Number.isNaN(dailyValue)) return currentValue;

    if (!inputRef || document.activeElement !== inputRef) {
      return formatCurrency(dailyValue, localProps.currency);
    }

    return dailyValue.toString();
  });

  const conversionText = createMemo(() => {
    const currentValue = localProps.fieldApi.state.value;
    if (!currentValue || currentValue === "") return null;

    const dailyValue = Number.parseFloat(parseInputValue(currentValue));

    if (Number.isNaN(dailyValue) || dailyValue === 0) return null;

    const hourlyValue = convertDailyToHourly(dailyValue);
    const hourlyFormatted = formatCurrency(hourlyValue, localProps.currency);
    const dailyFormatted = formatCurrency(dailyValue, localProps.currency);

    return `${dailyFormatted}/${t("common.day")} ≈ ${hourlyFormatted}/${t("common.hour")}`;
  });

  onMount(() => {
    if (inputRef) {
      const hasValue =
        inputRef.value.trim() !== "" && localProps.fieldApi.state.value;
      if (hasValue) {
        inputRef.dispatchEvent(
          new Event("blur", { bubbles: true, cancelable: true }),
        );
      }
    }
  });

  return (
    <div class="flex flex-col gap-2 w-full">
      <Input
        {...restProps}
        ref={inputRef}
        value={displayValue()}
        onInput={handleInput}
        onChange={handleInput}
        disabled={localProps.disabled}
        class={cn(localProps.class)}
        classList={localProps.classList}
      />

      <Show when={conversionText()}>
        <p class="text-xs text-muted-foreground">{conversionText()}</p>
      </Show>
    </div>
  );
};
